// The Data view inside a chart window (TODO §15/§4.2).
//
// A purpose-built table, and deliberately **no new dependency**: the requirement is a few
// hundred lines of inputs — add/remove/reorder rows and columns, rename headers, set a
// per-column type, arrow/Tab/Enter navigation and a clipboard paste — and every grid library
// that does that also brings virtualization, its own theming and a second styling system to
// keep in step with the deck stylesheet.
//
// **It renders in the slide's LOGICAL units and is then scaled by the canvas transform**, like
// everything else in a content window (§8.2). So the type here is sized in canvas px, not
// screen px, and it looks small in a narrow two-window slide for the same reason the chart
// does — which is correct: the author is editing at the size the audience will read.
//
// One rule the whole file follows: every edit goes through a pure function in `@/lib/chart`
// and comes back as a whole new `ChartDocument` this component hands upward. Nothing here
// mutates, and nothing here knows about Redux.

import { useCallback, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  addColumn,
  addRow,
  cellText,
  moveRow,
  parseClipboardTable,
  pasteTable,
  removeColumn,
  removeRow,
  renameColumn,
  setCell,
  setColumnType,
} from "@/lib/chart";
import { cn } from "@/lib/utils";
import type { ChartColumn, ChartDocument } from "@/types/chart";

export function ChartDataTable({
  doc,
  onChange,
}: {
  doc: ChartDocument;
  onChange: (doc: ChartDocument) => void;
}) {
  // Which cell has focus, so arrow keys know where they are. Held as `${row}:${col}` indices
  // rather than as ids: navigation is about POSITION in the grid, and ids would have to be
  // looked back up into positions on every key.
  const [focus, setFocus] = useState<{ row: number; col: number } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Move focus by delta, clamped. The DOM input is found by its data attributes rather than
  // by a ref array — a ref per cell in a 5000-row table is 5000 refs to keep in step with
  // every insert and delete, and the query runs on a keypress, not on a render.
  const moveFocus = useCallback((row: number, col: number) => {
    const next = gridRef.current?.querySelector<HTMLInputElement>(
      `input[data-row="${row}"][data-col="${col}"]`,
    );
    if (next) {
      next.focus();
      next.select();
    }
  }, []);

  const onCellKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    row: number,
    col: number,
  ) => {
    const last = doc.columns.length - 1;
    if (e.key === "ArrowDown" || e.key === "Enter") {
      e.preventDefault();
      // Enter on the last row appends one, so a table can be typed straight down without
      // reaching for the button. Arrow-down does not — it is navigation, not authoring.
      if (row === doc.rows.length - 1 && e.key === "Enter") {
        onChange(addRow(doc));
      }
      moveFocus(row + 1, col);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveFocus(row - 1, col);
    } else if (e.key === "ArrowLeft" && atStart(e.currentTarget)) {
      // Only when the caret is already at the edge, so left/right still move within a cell's
      // text — the behaviour of every spreadsheet, and the thing people notice is missing.
      e.preventDefault();
      moveFocus(row, col - 1);
    } else if (e.key === "ArrowRight" && atEnd(e.currentTarget)) {
      e.preventDefault();
      moveFocus(row, col + 1);
    } else if (e.key === "Tab" && !e.shiftKey && col === last) {
      // Tab off the end wraps to the next row's first cell, appending a row if there is none.
      e.preventDefault();
      if (row === doc.rows.length - 1) onChange(addRow(doc));
      moveFocus(row + 1, 0);
    }
  };

  // §4.2 A paste of more than one cell expands the table to fit and lands at the focused cell.
  // A single-cell paste falls through to the browser's own handling, so pasting one value into
  // one cell behaves exactly like typing it.
  const onCellPaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    row: number,
    col: number,
  ) => {
    const text = e.clipboardData.getData("text/plain");
    const block = parseClipboardTable(text);
    if (block.length === 0) return;
    if (block.length === 1 && block[0].length === 1) return;
    e.preventDefault();
    onChange(pasteTable(doc, block, row, col));
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <div ref={gridRef} className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {/* The row-handle column: reorder and delete live here rather than in a
                  hover-only overlay, which is unusable at a scaled-down slide size. */}
              <th className="w-16 border border-slate-200 bg-slate-50 p-1" />
              {doc.columns.map((column) => (
                <ColumnHeader
                  key={column.id}
                  column={column}
                  isX={doc.encoding.x === column.id}
                  canRemove={doc.columns.length > 1}
                  onRename={(label) =>
                    onChange(renameColumn(doc, column.id, label))
                  }
                  onType={(type) =>
                    onChange(setColumnType(doc, column.id, type))
                  }
                  onRemove={() => onChange(removeColumn(doc, column.id))}
                />
              ))}
              <th className="w-9 border border-slate-200 bg-slate-50 p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  aria-label="Add column"
                  title="Add column"
                  onClick={() => onChange(addColumn(doc))}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </th>
            </tr>
          </thead>
          <tbody>
            {doc.rows.map((row, r) => (
              <tr key={r}>
                <td className="border border-slate-200 bg-slate-50/70 p-0.5 align-middle">
                  <div className="flex items-center justify-center gap-0.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      aria-label="Move row up"
                      disabled={r === 0}
                      onClick={() => onChange(moveRow(doc, r, -1))}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0"
                      aria-label="Move row down"
                      disabled={r === doc.rows.length - 1}
                      onClick={() => onChange(moveRow(doc, r, 1))}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 text-slate-400 hover:text-red-600"
                      aria-label="Delete row"
                      onClick={() => onChange(removeRow(doc, r))}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </td>
                {doc.columns.map((column, c) => (
                  <td key={column.id} className="border border-slate-200 p-0">
                    <input
                      data-row={r}
                      data-col={c}
                      // Controlled: the doc is the source of truth and a keystroke round-trips
                      // through Redux before it comes back. That is what makes the chart
                      // redraw as you type — and what the §3.3.3 debounce exists to keep off
                      // the disk.
                      value={cellText(row[column.id])}
                      onChange={(e) =>
                        onChange(setCell(doc, r, column.id, e.target.value))
                      }
                      onKeyDown={(e) => onCellKeyDown(e, r, c)}
                      onPaste={(e) => onCellPaste(e, r, c)}
                      onFocus={() => setFocus({ row: r, col: c })}
                      className={cn(
                        "w-full bg-transparent px-1.5 py-1 outline-none",
                        // Numbers right, text left — the one formatting cue that makes a
                        // column of figures readable at a glance.
                        column.type === "number" && "text-right tabular-nums",
                        focus?.row === r &&
                          focus?.col === c &&
                          "bg-sky-50 ring-1 ring-inset ring-sky-400",
                      )}
                    />
                  </td>
                ))}
                <td className="border border-slate-200 bg-slate-50/70" />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onChange(addRow(doc))}
        >
          <Plus className="mr-1 h-3.5 w-3.5" /> Add row
        </Button>
        <span className="text-xs text-slate-400">
          {doc.rows.length} rows · paste a block from a spreadsheet to fill the
          table
        </span>
      </div>
    </div>
  );
}

function atStart(input: HTMLInputElement): boolean {
  return input.selectionStart === 0 && input.selectionEnd === 0;
}

function atEnd(input: HTMLInputElement): boolean {
  const end = input.value.length;
  return input.selectionStart === end && input.selectionEnd === end;
}

// A header is three controls in one cell: the label, the type, and remove. The X marker is a
// read-only cue — which column is the category axis is an ENCODING decision and belongs to
// the panel (§7.3), not to a click in the middle of a data table.
function ColumnHeader({
  column,
  isX,
  canRemove,
  onRename,
  onType,
  onRemove,
}: {
  column: ChartColumn;
  isX: boolean;
  canRemove: boolean;
  onRename: (label: string) => void;
  onType: (type: ChartColumn["type"]) => void;
  onRemove: () => void;
}) {
  return (
    <th className="min-w-24 border border-slate-200 bg-slate-50 p-0 align-top">
      <div className="flex items-center gap-0.5 px-1 pt-1">
        <input
          value={column.label}
          onChange={(e) => onRename(e.target.value)}
          // §3.2.1 A rename touches the label only — the encoding holds the id, so this can
          // never blank the chart.
          className="min-w-0 flex-1 bg-transparent text-[13px] font-medium outline-none"
          aria-label="Column name"
        />
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 shrink-0 p-0 text-slate-400 hover:text-red-600"
          aria-label={`Delete column ${column.label}`}
          disabled={!canRemove}
          onClick={onRemove}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
      <div className="flex items-center gap-1 px-1 pb-1">
        <select
          value={column.type}
          onChange={(e) => onType(e.target.value as ChartColumn["type"])}
          className="w-full bg-transparent text-[11px] font-normal text-slate-500 outline-none"
          aria-label={`Type of column ${column.label}`}
        >
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
        </select>
        {isX && (
          <span
            className="shrink-0 rounded bg-slate-200 px-1 text-[10px] font-medium text-slate-600"
            title="This column is the chart's category axis"
          >
            X
          </span>
        )}
      </div>
    </th>
  );
}
